// src/modules/competitions/components/TermsAcceptance.tsx - VERSIÓN CORREGIDA

import React, { useState, useEffect, useRef } from 'react';
import Card from '../../../shared/components/ui/Card';
import Loading from '../../../shared/components/Loading';
import ErrorMessage from '../../../shared/components/ErrorMessage';
import { TermsAndConditions } from '../types';
import { competitionService } from '../services/competitionService';
import Button from '../../../shared/components/ui/Button';
import '../styles/termsRegistration.css';

interface TermsAcceptanceProps {
  competitionId: number;
  onAccept: () => Promise<void>;
  onBack?: () => void;
  isLoading?: boolean;
  error?: string | null;
}

const TermsAcceptance: React.FC<TermsAcceptanceProps> = ({
  competitionId,
  onAccept,
  onBack,
  isLoading = false,
  error = null
}) => {
  const [terms, setTerms] = useState<TermsAndConditions | null>(null);
  const [loadingTerms, setLoadingTerms] = useState(true);
  const [termsError, setTermsError] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const termsTextRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadTerms();
  }, [competitionId]);

  useEffect(() => {
    // Verificar si el contenido necesita scroll
    checkScrollNeeded();
  }, [terms]);

  const loadTerms = async () => {
    try {
      setLoadingTerms(true);
      setTermsError(null);
      
      const response = await competitionService.getTermsAndConditions(competitionId, 'participant');
      
      if (response.success && response.data) {
        setTerms(response.data);
      } else {
        setTermsError(response.message || 'Error al cargar términos y condiciones');
      }
    } catch (err) {
      setTermsError('Error al cargar términos y condiciones');
    } finally {
      setLoadingTerms(false);
    }
  };

  const checkScrollNeeded = () => {
    if (termsTextRef.current) {
      const element = termsTextRef.current;
      // Si el contenido no necesita scroll (es más corto que el contenedor), marcar como scrolleado
      if (element.scrollHeight <= element.clientHeight + 10) {
        setHasScrolled(true);
      }
    }
  };

  const handleScrollTerms = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;
    
    // Verificar si llegó al final (con margen de 10px)
    const isScrolledToBottom = scrollTop + clientHeight >= scrollHeight - 10;
    
    if (isScrolledToBottom && !hasScrolled) {
      setHasScrolled(true);
    }
  };

  const handleAccept = async () => {
    if (terms && agreed) {
      await onAccept();
    }
  };

  if (loadingTerms) {
    return (
      <Card className="terms-card" variant="elevated">
        <Loading message="Cargando términos y condiciones..." />
      </Card>
    );
  }

  if (termsError || !terms) {
    return (
      <Card className="terms-card" variant="elevated">
        <ErrorMessage 
          message={termsError || 'No se pudieron cargar los términos'} 
          type="error"
        />
        {onBack && (
          <Button className="btn btn-outline" onClick={onBack}>
            Volver
          </Button>
        )}
      </Card>
    );
  }

  return (
    <Card className="terms-card" variant="elevated">
      <div className="terms-header">
        <h2>Términos y Condiciones</h2>
        <div className="terms-meta">
          <span className="terms-version">Versión {terms.version}</span>
          <span className="terms-date">
            Vigente desde: {new Date(terms.effective_date).toLocaleDateString('es-ES')}
          </span>
        </div>
      </div>

      {error && (
        <ErrorMessage 
          message={error} 
          type="error"
        />
      )}

      <div className="terms-content">
        <h3>{terms.title}</h3>
        
        <div 
          ref={termsTextRef}
          className="terms-text"
          onScroll={handleScrollTerms}
        >
          {terms.content.split('\n').map((paragraph, index) => (
            paragraph.trim() && (
              <p key={index} className="terms-paragraph">
                {paragraph}
              </p>
            )
          ))}
        </div>

        {!hasScrolled && (
          <div className="scroll-indicator">
            ⬇️ Desplázate hacia abajo para leer todos los términos ⬇️
          </div>
        )}
      </div>

      <div className="terms-actions">
        <div className="terms-checkbox">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              disabled={!hasScrolled}
            />
            <span className="checkbox-text">
              He leído y acepto todos los términos y condiciones descritos anteriormente
            </span>
          </label>
          
          {!hasScrolled && (
            <p className="checkbox-note">
              ⚠️ Debes leer completamente los términos antes de poder aceptarlos
            </p>
          )}
        </div>

        <div className="action-buttons">
          {onBack && (
            <Button
              type="button"
              className="btn btn-outline"
              onClick={onBack}
              disabled={isLoading}
            >
              Volver
            </Button>
          )}
          
          <Button
            type="button"
            className={`btn btn-primary ${(!agreed || !hasScrolled) ? 'disabled' : ''}`}
            onClick={handleAccept}
            disabled={!agreed || !hasScrolled || isLoading}
          >
            {isLoading ? 'Procesando...' : 'Aceptar y Continuar'}
          </Button>
        </div>
      </div>

      <div className="terms-footer">
        <p className="terms-note">
          Al aceptar estos términos, confirmas que has leído, entendido y estás de acuerdo 
          con todas las condiciones establecidas para tu participación en esta competencia.
        </p>
      </div>
    </Card>
  );
};

export default TermsAcceptance;